// =============================================================================
// schedule-rules — pure helpers for the opening-hours editor (ScheduleTab)
// =============================================================================
// booking.location_schedule_rule always stores a non-empty day_of_week array.
// For regular rules the operator picks those weekdays by hand. For OVERRIDE
// rules ("vervang andere regels op deze datums") the natural question is a
// date or a date range, not a weekday pattern, so the editor derives the
// weekday set from the range unless the operator explicitly asks for a
// pattern. These helpers hold that derivation and the "this rule can never
// apply" check, so both are unit-testable without React.

export const ALL_WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function utcMidnight(iso: string): Date | null {
  if (!ISO_DATE.test(iso)) return null;
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO weekday (1 = Monday … 7 = Sunday) of a YYYY-MM-DD date; null when malformed. */
export function isoWeekdayOf(iso: string): number | null {
  const d = utcMidnight(iso);
  if (!d) return null;
  const js = d.getUTCDay(); // 0 = Sunday
  return js === 0 ? 7 : js;
}

/**
 * The ISO weekdays that occur at least once inside [from, to] (inclusive).
 * Empty when either date is malformed or the range is inverted; the caller
 * reports those separately.
 */
export function weekdaysInRange(from: string, to: string): Set<number> {
  const out = new Set<number>();
  const a = utcMidnight(from);
  const b = utcMidnight(to);
  const start = isoWeekdayOf(from);
  if (!a || !b || start === null || b.getTime() < a.getTime()) return out;
  const days = Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1;
  for (let i = 0; i < Math.min(days, 7); i++) {
    out.add(((start - 1 + i) % 7) + 1);
  }
  return out;
}

/**
 * True when none of `days` falls inside [from, to]: the rule would never
 * apply to any date (e.g. a one-day rule on a Thursday with Thursday
 * unticked). False for an invalid range, which is reported on its own.
 */
export function ruleNeverMatches(days: Iterable<number>, from: string, to: string): boolean {
  const inRange = weekdaysInRange(from, to);
  if (inRange.size === 0) return false;
  for (const d of days) if (inRange.has(d)) return false;
  return true;
}

export interface DayDerivation {
  days:       Set<number>;
  validFrom:  string;
  validUntil: string;
  isOverride: boolean;
  /** Override only: the operator chose a weekday pattern instead of "every day in the range". */
  customDays: boolean;
}

/** Sorted day_of_week to persist: derived from the range for pattern-less overrides, else as picked. */
export function effectiveDays(rule: DayDerivation): number[] {
  const set = rule.isOverride && !rule.customDays
    ? weekdaysInRange(rule.validFrom, rule.validUntil)
    : rule.days;
  return Array.from(set).sort((a, b) => a - b);
}

/** Whether a stored override's day set is anything other than "every day of its range". */
export function isCustomDayPattern(days: Iterable<number>, from: string, to: string): boolean {
  const stored = new Set(days);
  const derived = weekdaysInRange(from, to);
  if (stored.size === ALL_WEEKDAYS.length) return false;
  if (stored.size !== derived.size) return true;
  for (const d of stored) if (!derived.has(d)) return true;
  return false;
}

/** 'vrijdag 4 december 2026', for the derived single-date label. */
export function formatDutchDate(iso: string): string {
  const d = utcMidnight(iso);
  if (!d) return iso;
  return new Intl.DateTimeFormat('nl-NL', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(d);
}
